import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, RefreshCw, Search, Radio, Settings } from 'lucide-react';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { authService } from '../services/authService';
import {
  logSistemaService, AmbientesLogResponse, LogResponse, LinhaLog, NivelLog
} from '../services/logSistemaService';
import ConfigLogsDialog from '../components/logs/ConfigLogsDialog';
import { toast } from '../hooks/use-toast';

const NIVEIS: NivelLog[] = ['ERROR', 'WARN', 'INFO', 'DEBUG'];
const CATEGORIAS = ['app', 'http', 'webhook', 'console', 'backup'];
const LIMITES = [200, 500, 1000, 2000];
const AO_VIVO_MS = 5000;

const COR_NIVEL: Record<NivelLog, string> = {
  ERROR: 'text-red-400',
  WARN: 'text-amber-300',
  INFO: 'text-sky-300',
  DEBUG: 'text-slate-500'
};

const filtroBtn = (ativo: boolean) =>
  `px-3 py-1 rounded-md text-xs font-medium transition-colors ${ativo
    ? 'bg-sky-600 text-white'
    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`;

const chip = 'flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1';

function fmtTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtHora(ts: string | null): string {
  if (!ts) return '--:--:--';
  const d = new Date(ts);
  const p = (n: number, l = 2) => String(n).padStart(l, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`;
}

function fmtData(data: string): string {
  const [a, m, d] = data.split('-');
  return `${d}/${m}/${a}`;
}

const LinhaView: React.FC<{ linha: LinhaLog }> = ({ linha }) => {
  const [aberta, setAberta] = useState(false);
  const temMeta = !!linha.meta && Object.keys(linha.meta).length > 0;
  const metaResumo = temMeta ? JSON.stringify(linha.meta) : '';

  return (
    <div
      className={`px-3 py-0.5 hover:bg-white/5 ${linha.level === 'ERROR' ? 'bg-red-500/10' : ''} ${temMeta ? 'cursor-pointer' : ''}`}
      onClick={() => temMeta && setAberta((v) => !v)}
    >
      <div className="flex gap-2 items-start">
        <span className="text-slate-500 shrink-0">{fmtHora(linha.ts)}</span>
        <span className={`w-12 shrink-0 font-semibold ${COR_NIVEL[linha.level] || 'text-slate-300'}`}>{linha.level}</span>
        <span className="w-16 shrink-0 text-violet-300">{linha.cat}</span>
        <span className="text-slate-100 whitespace-pre-wrap break-all min-w-0">
          {linha.msg}
          {temMeta && !aberta && (
            <span className="text-slate-500"> {metaResumo.length > 180 ? `${metaResumo.slice(0, 180)}…` : metaResumo}</span>
          )}
        </span>
      </div>
      {temMeta && aberta && (
        <pre className="ml-[8.5rem] mt-1 mb-1 p-2 rounded bg-black/40 text-emerald-200 whitespace-pre-wrap break-all">
          {JSON.stringify(linha.meta, null, 2)}
        </pre>
      )}
    </div>
  );
};

const LogsSistemaPage: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [ambientes, setAmbientes] = useState<AmbientesLogResponse | null>(null);
  const [ambiente, setAmbiente] = useState('');
  const [data, setData] = useState('');
  const [niveis, setNiveis] = useState<NivelLog[]>([]);
  const [cats, setCats] = useState<string[]>([]);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [limite, setLimite] = useState(500);
  const [aoVivo, setAoVivo] = useState(false);
  const [configAberta, setConfigAberta] = useState(false);

  const [resp, setResp] = useState<LogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const viewerRef = useRef<HTMLDivElement>(null);
  const colarNoFim = useRef(true);

  useEffect(() => {
    if (user && !isSuperAdmin) navigate('/dashboard');
  }, [user, isSuperAdmin, navigate]);

  const carregarAmbientes = useCallback(async () => {
    try {
      const r = await logSistemaService.getAmbientes();
      setAmbientes(r);
      setAmbiente((atual) => atual || r.atual || r.ambientes[0]?.ambiente || '');
    } catch (error) {
      console.error('Erro ao carregar ambientes de log:', error);
      toast({ title: 'Não foi possível carregar os ambientes de log.', variant: 'destructive' });
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) carregarAmbientes();
  }, [isSuperAdmin, carregarAmbientes]);

  const dias = useMemo(
    () => ambientes?.ambientes.find((a) => a.ambiente === ambiente)?.dias ?? [],
    [ambientes, ambiente]
  );

  // Ao trocar de ambiente, seleciona o dia mais recente que ele tem
  useEffect(() => {
    if (!ambiente) return;
    setData((atual) => (dias.some((d) => d.data === atual) ? atual : dias[0]?.data ?? ''));
  }, [ambiente, dias]);

  const carregar = useCallback(async (silencioso = false) => {
    if (!ambiente || !data) {
      setResp(null);
      setLoading(false);
      return;
    }
    if (!silencioso) setLoading(true); else setRefreshing(true);
    try {
      const r = await logSistemaService.getLogs({
        ambiente, data, limite,
        nivel: niveis.join(',') || undefined,
        cat: cats.join(',') || undefined,
        busca: buscaAplicada || undefined
      });
      setResp(r);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast({ title: 'Não foi possível carregar os logs.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [ambiente, data, limite, niveis, cats, buscaAplicada]);

  useEffect(() => {
    if (isSuperAdmin) carregar();
  }, [isSuperAdmin, carregar]);

  useEffect(() => {
    if (!aoVivo) return undefined;
    const id = setInterval(() => { carregar(true); carregarAmbientes(); }, AO_VIVO_MS);
    return () => clearInterval(id);
  }, [aoVivo, carregar, carregarAmbientes]);

  // Mantém o fim do log visível, a não ser que o usuário tenha rolado para cima
  useEffect(() => {
    const el = viewerRef.current;
    if (el && colarNoFim.current) el.scrollTop = el.scrollHeight;
  }, [resp]);

  const onScroll = () => {
    const el = viewerRef.current;
    if (el) colarNoFim.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const alternar = <T,>(lista: T[], item: T): T[] => (lista.includes(item) ? lista.filter((i) => i !== item) : [...lista, item]);

  const baixar = async () => {
    try {
      await logSistemaService.baixar(ambiente, data);
    } catch (error) {
      toast({ title: 'Não foi possível baixar o arquivo.', variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-slate-900 overflow-hidden">
      <Header titulo="Logs do Sistema" user={user} />

      <div className="flex-1 min-h-0 p-4 md:p-6 flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/configuracoes')}>
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div className={`${chip} flex-wrap`}>
            {(ambientes?.ambientes ?? []).map((a) => (
              <button key={a.ambiente} className={filtroBtn(ambiente === a.ambiente)} onClick={() => setAmbiente(a.ambiente)}>
                {a.ambiente.toUpperCase()}
                {a.ambiente === ambientes?.atual && <span className="ml-1 opacity-70">(este)</span>}
              </button>
            ))}
            {ambientes && ambientes.ambientes.length === 0 && (
              <span className="px-3 py-1 text-xs text-gray-500">Nenhum ambiente com logs ainda</span>
            )}
          </div>
          <div className={`${chip} flex-wrap`}>
            {dias.map((d) => (
              <button key={d.data} className={filtroBtn(data === d.data)} onClick={() => setData(d.data)} title={fmtTamanho(d.tamanhoBytes)}>
                {fmtData(d.data).slice(0, 5)}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { carregar(true); carregarAmbientes(); }} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant={aoVivo ? 'default' : 'outline'} size="sm" className="gap-1.5" onClick={() => setAoVivo((v) => !v)}>
            <Radio className={`h-4 w-4 ${aoVivo ? 'animate-pulse' : ''}`} />
            Ao vivo
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={baixar} disabled={!ambiente || !data}>
            <Download className="h-4 w-4" />
            Baixar
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 ml-auto" onClick={() => setConfigAberta(true)}>
            <Settings className="h-4 w-4" />
            Configurações
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className={chip}>
            <button className={filtroBtn(niveis.length === 0)} onClick={() => setNiveis([])}>Todos</button>
            {NIVEIS.map((n) => (
              <button key={n} className={filtroBtn(niveis.includes(n))} onClick={() => setNiveis(alternar(niveis, n))}>{n}</button>
            ))}
          </div>
          <div className={chip}>
            <button className={filtroBtn(cats.length === 0)} onClick={() => setCats([])}>Todas</button>
            {CATEGORIAS.map((c) => (
              <button key={c} className={filtroBtn(cats.includes(c))} onClick={() => setCats(alternar(cats, c))}>{c}</button>
            ))}
          </div>
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); setBuscaAplicada(busca.trim()); }}
          >
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar no texto e nos detalhes" className="h-8 w-64 text-xs" />
            <Button type="submit" size="sm" variant="outline" className="gap-1.5"><Search className="h-4 w-4" />Buscar</Button>
          </form>
          <select
            value={limite}
            onChange={(e) => setLimite(Number(e.target.value))}
            className="h-8 text-xs rounded-md border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 px-2"
          >
            {LIMITES.map((l) => <option key={l} value={l}>Últimas {l}</option>)}
          </select>
          <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
            {resp
              ? `${resp.truncado ? `${resp.linhas.length} de ${resp.total}` : resp.total} linha(s) · ${fmtTamanho(resp.tamanhoBytes)}`
              : ''}
            {ambientes ? ` · guarda ${ambientes.retencaoDias} dias · ${fmtTamanho(ambientes.tamanhoTotalBytes)} no total` : ''}
          </span>
        </div>

        <div
          ref={viewerRef}
          onScroll={onScroll}
          className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-700 bg-slate-950 py-2 font-mono text-[12px] leading-5"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full text-slate-400 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          ) : !resp || resp.linhas.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-500">
              {ambiente && data ? 'Nenhuma linha para este filtro.' : 'Selecione um ambiente e um dia.'}
            </div>
          ) : (
            <>
              {resp.truncado && (
                <div className="px-3 pb-2 text-amber-300/80">
                  Mostrando as últimas {resp.linhas.length} de {resp.total} linhas. Use os filtros ou baixe o arquivo para ver tudo.
                </div>
              )}
              {resp.linhas.map((l, i) => <LinhaView key={`${l.ts}-${i}`} linha={l} />)}
            </>
          )}
        </div>
      </div>
      <ConfigLogsDialog
        open={configAberta}
        onOpenChange={setConfigAberta}
        onSalvo={() => { carregarAmbientes(); carregar(true); }}
      />
    </div>
  );
};

export default LogsSistemaPage;
